import {
  CAREERS_GERMANY_FLAG_ALT,
  CAREERS_GERMANY_FLAG_TITLE,
} from "@/lib/careers-seo";
import { useTranslations } from "next-intl";
import Image from "next/image";
import type { ReactElement } from "react";
import { CAREERS_BLUR_DARK } from "../../_components/careers-image-blur";
import { FunnelCurveArrow } from "./developers-funnel-arrow";
import type { FunnelImageSlot } from "./developers-funnel-content";

const GERMANY_FLAG_SRC: string = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 10"><rect width="15" height="3.33" fill="#000"/><rect y="3.33" width="15" height="3.34" fill="#DD0000"/><rect y="6.67" width="15" height="3.33" fill="#FFCE00"/></svg>',
)}`;

/**
 * Curved arrow + rounded label under a photo. Lives fully below the frame
 * so nothing is clipped; arrow points up at the bottom-left tile.
 */
function GermanyFlag(): ReactElement {
  return (
    // eslint-disable-next-line @next/next/no-img-element -- 14×10 flag; needs real alt/title for SEO
    <img
      className="dvf-media-caption-flag"
      src={GERMANY_FLAG_SRC}
      width={14}
      height={10}
      alt={CAREERS_GERMANY_FLAG_ALT}
      title={CAREERS_GERMANY_FLAG_TITLE}
    />
  );
}

function FunnelImageCaption({
  text,
}: {
  readonly text: string;
}): ReactElement {
  return (
    <p className="dvf-media-caption">
      <FunnelCurveArrow className="dvf-media-caption-arrow" />
      <span className="dvf-media-caption-pill">
        {text}
        {text.includes("Germany") ? <GermanyFlag /> : null}
      </span>
    </p>
  );
}

/**
 * Photo from a content slot. Optional caption sits under the frame.
 */
export function FunnelImage({
  slot,
  className,
  priority = false,
  sizes = "100vw",
}: {
  readonly slot: FunnelImageSlot;
  readonly className: string;
  readonly priority?: boolean;
  readonly sizes?: string;
}): ReactElement {
  const objectFit: "cover" | "contain" = slot.objectFit ?? "cover";
  const mediaClassName: string =
    objectFit === "contain" ? "dvf-media dvf-media-contain" : "dvf-media";
  const caption: string | undefined = slot.caption;

  return (
    <div className={`dvf-media-block ${className}`}>
      <div className={mediaClassName} style={{ aspectRatio: slot.ratio }}>
        <Image
          src={slot.src}
          alt={slot.alt}
          title={slot.title}
          fill
          sizes={sizes}
          priority={priority}
          placeholder="blur"
          blurDataURL={CAREERS_BLUR_DARK}
          style={{ objectFit }}
        />
      </div>
      {caption !== undefined && caption.length > 0 ? (
        <FunnelImageCaption text={caption} />
      ) : null}
    </div>
  );
}
